import { call, CallEffect, put, PutEffect, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { t } from 'i18next';
import client from '../../service/graphql';
import {
  GET_COACH,
  GET_ORGANISATION_COACHES,
  CREATE_COACH_MUTATION,
  APPROVE_COACH,
  REJECT_COACH,
  UPDATE_COACH_LANGUAGE_MUTATION,
  UPDATE_COACH_STATUS_MUTATION,
  ARCHIVE_COACH_MUTATION,
  UPDATE_COACH,
  UPDATE_COACH_PROFILE_PICTURE,
  GET_COACH_SETTINGS,
  UPDATE_COACH_SETTINGS,
  COACH_DATA_REQUEST,
  COACH_DELETE_REQUEST,
  GET_BRAND_CONFIGURATIONS,
  GET_COACH_SERVICE_PACKAGES,
  CREATE_COACH_SERVICE_PACKAGE,
  UPDATE_COACH_SERVICE_PACKAGE,
  DELETE_COACH_SERVICE_PACKAGE,
  GET_COACH_EVENT_DETAILS,
  UPDATE_COACH_EVENT_DETAILS,
} from '../../service/graphql/coachQuery';
import {
  addCoachFailure,
  addCoachRequest,
  addCoachSuccess,
  coachLoginFailure,
  coachLoginRequest,
  coachLoginSuccess,
  fetchCoachesFailure,
  fetchCoachesRequest,
  fetchCoachesSuccess,
  fetchCoachFailure,
  fetchCoachRequest,
  fetchCoachSuccess,
  ICoach,
  IFetchCoachesPayload,
  ICreateCoachInput,
  ILoggedUser,
  passwordResetRequest,
  approveCoachFailure,
  approveCoachRequest,
  approveCoachSuccess,
  setAdminLoginSuccess,
  passwordResetSuccess,
  passwordResetFailure,
  rejectCoachSuccess,
  rejectCoachFailure,
  rejectCoachRequest,
  fetchUpdateCoachLanguageSuccess,
  fetchUpdateCoachLanguageFailure,
  fetchUpdateCoachLanguageRequest,
  IUpdateCoachLanguagePayload,
  changeCoachStatus,
  IChangeCoachStatusPayload,
  changeCoachStatusSuccess,
  changeCoachStatusFailure,
  archiveCoachRequest,
  archiveCoachSuccess,
  archiveCoachFailure,
  fetchArchivedCoaches,
  fetchArchivedCoachesSuccess,
  updateCoach,
  IUpdateCoachPayload,
  updateCoachSuccess,
  updateCoachFailure,
  fetchCoachSettings,
  fetchCoachSettingsSuccess,
  fetchCoachSettingsFailure,
  ICoachAISettings,
  updateCoachSettings,
  updateCoachSettingsSuccess,
  IUpdateCoachAISettings,
  updateCoachSettingsFailure,
  getSocialSignInURL,
  getSocialSignInURLSuccess,
  getSocialSignInURLFailure,
  socialSignInRequest,
  socialSignInSuccess,
  socialSignInFailure,
  coachDataRequest,
  coachDataRequestSuccess,
  coachDataRequestFailure,
  coachDeleteRequest,
  coachDeleteRequestSuccess,
  coachDeleteRequestFailure,
  IArchiveCoachPayload,
  getBrandConfigurations,
  IGetBrandConfigurationsResponse,
  getBrandConfigurationsSuccess,
  getBrandConfigurationsFailure,
  getCoachServicePackagesSuccess,
  getCoachServicePackagesFailure,
  ICreateServicePackageInput,
  createCoachServicePackageSuccess,
  IUpdateServicePackageInput,
  updateCoachServicePackageSuccess,
  updateCoachServicePackageFailure,
  deleteCoachServicePackageSuccess,
  deleteCoachServicePackageFailure,
  getCoachServicePackagesRequest,
  createCoachServicePackageRequest,
  updateCoachServicePackageRequest,
  deleteCoachServicePackageRequest,
  IReturnCoachServicePackages,
  getCoachEventDetailsSuccess,
  getCoachEventDetailsFailure,
  IAddCoachEventSettingsInput,
  updateCoachEventDetailsSuccess,
  updateCoachEventDetailsFailure,
  getCoachEventDetailsRequest,
  updateCoachEventDetailsRequest,
  ICreateServicePackageResponse,
  IUpdateServicePackageResponse,
  createCoachServicePackageFailure,
  ICreatePasswordInput,
  createPasswordSuccess,
  createPasswordFailure,
  createPasswordRequest,
} from './coachSlice';
import { IGraphQLResponse, UserPermissionType } from '../../types/commonTypes';
import {
  changeUserPassword,
  coachLogin,
  socialSignIn,
  IChangeUserPasswordParams,
  ILoginResponse,
  IRedirectUrlReponse,
  redirectUrl,
} from '../../service/api/authApi';
import { toastStyles, transformCoachesServerData } from '../../utils/utils';
import { UserType } from '../../components/User/User';
import i18n from '../../i18n';
import { IdentityProviderEnum } from '../../types/coach';
import { LOGIN_ROUTE, SIGNUP_ROUTE } from '../../utils/routes';
import { removeBrandConfigurations } from '../../utils/brandConfigurationsUtils';
import { getGraphQLError } from '../../utils/errorMap';
import { fetchCoachGroupRequest } from '../coachGroup/coachGroupSlice';
import { logout } from '../GlobalAction';

interface IGetCoachResponse {
  getCoach: ICoach;
}

interface IUpdateCoachLanguageResponse {
  updateCoachLanguage: ICoach;
}

export interface IGetAllCoachesReponse {
  getOrganizationCoaches: {
    totalClientCount: number;
    coaches: ICoach[];
  };
}

export interface IGetAllCoachesData {
  totalClientCount: number;
  coaches: ICoach[];
}
interface ICreateCoachResponse {
  createCoach: ICoach;
}

export interface IChangeCoachStatusResponse {
  changeCoachStatus: {
    id: string;
    isActive: boolean;
  };
}

interface IArchieveCoachResponse {
  archiveCoach: {
    success: boolean;
    message: string;
    statusCode: number;
  };
}

interface IUpdateCoachProfilePicResponse {
  updateCoachProfilePic: {
    profilePicLink: {
      contentType: string;
      fileName: string;
      url: string;
      key: string;
    };
  };
}

interface IGetCoachSettingsResponse {
  getCoachSettings: ICoachAISettings;
}

async function saveprofilePicInS3({
  fileObject,
  preSignedUrl,
}: {
  fileObject: File | null;
  preSignedUrl: string;
}): Promise<void> {
  if (fileObject?.name) {
    await axios.put(preSignedUrl, fileObject, {
      headers: {
        'Content-Type': `${fileObject.type}`,
      },
    });
  }
}

async function createCoachPassword(payload: ICreatePasswordInput): Promise<AxiosResponse> {
  return axios.post(`${process.env.REACT_APP_AUTH_SERVICE}/auth/create-password`, payload);
}

function* watchFetchCoach() {
  try {
    const result: IGraphQLResponse<IGetCoachResponse> = yield call(client.query.bind(client), {
      query: GET_COACH,
    });
    yield put(fetchCoachSuccess(result.data.getCoach));

    const hasPermission = result?.data?.getCoach?.userGroup?.modules.some((module) =>
      module.UserPermissions.some((permission) =>
        [UserPermissionType.CORE_MODULE_VIEW_APP_SETTINGS].includes(
          permission.permissionCode as UserPermissionType
        )
      )
    );

    if (hasPermission) {
      yield put(fetchCoachSettings(result.data.getCoach.id));
    }
  } catch (error: unknown) {
    const graphQLError = getGraphQLError(error);
    if (graphQLError) {
      yield put(fetchCoachFailure(graphQLError.message));
      toast.error(graphQLError.message, toastStyles.error);
    } else {
      yield put(fetchCoachFailure(t('auth.toastMessage.unknownError')));
    }
  }
}

function* watchUpdateCoachLanguage({ payload }: PayloadAction<IUpdateCoachLanguagePayload>) {
  try {
    const { coachId, lang } = payload;
    const result: IGraphQLResponse<IUpdateCoachLanguageResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: UPDATE_COACH_LANGUAGE_MUTATION,
        variables: { coachId, lang },
      }
    );
    yield put(fetchUpdateCoachLanguageSuccess(result.data.updateCoachLanguage));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchUpdateCoachLanguageFailure(error.message));
  }
}

function* watchCoachLogin({
  payload,
}: PayloadAction<{ username: string; password: string }>): Generator<
  CallEffect | PutEffect,
  void,
  ILoginResponse
> {
  try {
    const { username, password } = payload;
    const response = yield call(coachLogin, username, password);
    const data = yield response.json();
    if (response.ok && data.userId) {
      yield put(coachLoginSuccess(data as ILoggedUser));
      if (data.userType === UserType.COACH) {
        yield put(fetchCoachRequest());
      }
      if (data.userType === UserType.ADMIN) {
        yield put(setAdminLoginSuccess());
      }
    } else {
      // get status code
      if (response.status === 401) {
        toast.error(i18n.t('auth.toastMessage.loginUnsuccessful'), toastStyles.error);
      }
      throw new Error(data.message || 'Failed to login');
    }
  } catch (error: unknown) {
    if (error instanceof Error) yield put(coachLoginFailure(error.toString()));
  }
}

function* watchPasswordReset({
  payload,
}: PayloadAction<IChangeUserPasswordParams>): Generator<
  CallEffect | PutEffect,
  void,
  ILoginResponse
> {
  try {
    const { username, oldPassword, newPassword, confirmPassword } = payload;
    const response = yield call(changeUserPassword, {
      username,
      oldPassword,
      newPassword,
      confirmPassword,
    });
    const data = yield response.json();
    if (response.ok) {
      yield put(passwordResetSuccess());
      yield put(logout());
      toast.success(i18n.t('auth.toastMessage.passwordChangeSuccessful'), toastStyles.success);
    } else {
      yield put(passwordResetFailure());
      // get status code
      switch (response.status) {
        case 400:
          if (data.message) {
            toast.error(data.message, toastStyles.error);
          }
          break;
        case 401:
          toast.error(i18n.t('auth.toastMessage.passwordIncorrect'), toastStyles.error);
          break;
        default:
          toast.error(data.message || i18n.t('auth.toastMessage.unknownError'), toastStyles.error);
      }
    }
  } catch (error: unknown) {
    yield put(passwordResetFailure());
    if (error instanceof Error) toast.error(error.message, toastStyles.error);
  }
}

function* watchFetchCoaches({ payload }: PayloadAction<IFetchCoachesPayload>) {
  const { organizationId, nameText, skip, take, status, archived, brandTag } = payload;
  try {
    const result: IGraphQLResponse<IGetAllCoachesReponse> = yield call(client.query.bind(client), {
      query: GET_ORGANISATION_COACHES,
      variables: { organizationId, take, skip, nameText, status, isArchived: archived, brandTag },
    });

    const transformedCoaches = transformCoachesServerData(result.data.getOrganizationCoaches);
    if (archived) {
      yield put(fetchArchivedCoachesSuccess(transformedCoaches));
    } else {
      yield put(fetchCoachesSuccess(transformedCoaches));
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(fetchCoachesFailure(error.message));
    }
  }
}

function* watchApproveCoach({ payload }: PayloadAction<{ coachId: string }>) {
  const { coachId } = payload;
  try {
    yield call(client.mutate.bind(client), {
      mutation: APPROVE_COACH,
      variables: { coachId },
    });
    toast.success(i18n.t('coachesTable.toastMessage.applicationAccepted'), toastStyles.success);
    yield put(approveCoachSuccess());
    yield put(
      fetchCoachesRequest({
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
        skip: 0,
        take: 10,
        nameText: '',
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(approveCoachFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchAddCoach({ payload }: PayloadAction<ICreateCoachInput>) {
  try {
    const { CreateCoach } = payload;
    const result: IGraphQLResponse<ICreateCoachResponse> = yield call(client.mutate.bind(client), {
      mutation: CREATE_COACH_MUTATION,
      variables: { CreateCoach },
    });
    toast.success(i18n.t('addNewCoach.toastMessage.coachAdded'), toastStyles.success);
    yield put(addCoachSuccess(result.data.createCoach));
    yield put(
      fetchCoachesRequest({
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
        skip: 0,
        take: 10,
        nameText: '',
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(addCoachFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchRejectCoach({ payload }: PayloadAction<{ coachId: string }>) {
  const { coachId } = payload;
  try {
    yield call(client.mutate.bind(client), {
      mutation: REJECT_COACH,
      variables: { coachId },
    });
    yield put(rejectCoachSuccess());
    toast.success(i18n.t('applicationView.toastMessage.applicationRejected'), toastStyles.error);
    yield put(
      fetchCoachesRequest({
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
        skip: 0,
        take: 10,
        nameText: '',
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(rejectCoachFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchChangeCoachStatus({ payload }: PayloadAction<IChangeCoachStatusPayload>) {
  const { coachId, status } = payload;
  try {
    const result: IGraphQLResponse<IChangeCoachStatusResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: UPDATE_COACH_STATUS_MUTATION,
        variables: {
          coachId,
          status,
        },
      }
    );
    if (result.data.changeCoachStatus.isActive) {
      toast.success(i18n.t('coachesTable.toastMessage.coachActivated'), toastStyles.success);
    } else {
      toast.error(i18n.t('coachesTable.toastMessage.coachDeactivated'), toastStyles.error);
    }
    yield put(changeCoachStatusSuccess(result.data.changeCoachStatus.isActive));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(changeCoachStatusFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchArchieveCoachRequest({ payload }: PayloadAction<IArchiveCoachPayload>) {
  try {
    const result: IGraphQLResponse<IArchieveCoachResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: ARCHIVE_COACH_MUTATION,
        variables: {
          archiveCoachInput: {
            coachId: payload.coachId,
            password: payload.password,
          },
        },
      }
    );
    if (result.data.archiveCoach.statusCode === 200) {
      toast.success(i18n.t('coachesTable.archiveCoach.toastMessage'), toastStyles.success);
      yield put(
        fetchCoachGroupRequest({
          take: 10,
          skip: 0,
          isactiveCoaches: true,
        })
      );
    } else {
      toast.error(result.data.archiveCoach.message, toastStyles.error);
    }
    yield put(archiveCoachSuccess(result.data.archiveCoach.success));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(archiveCoachFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchUpdateCoach({ payload }: PayloadAction<IUpdateCoachPayload>) {
  try {
    const { id, firstName, lastName, tp, timeZone, profilePicFile } = payload;
    if (profilePicFile) {
      const result: IGraphQLResponse<IUpdateCoachProfilePicResponse> = yield call(
        client.mutate.bind(client),
        {
          mutation: UPDATE_COACH_PROFILE_PICTURE,
          variables: { id, fileName: profilePicFile.name, contentType: profilePicFile.type },
        }
      );

      const payloadObj = {
        fileObject: profilePicFile,
        preSignedUrl: result.data.updateCoachProfilePic.profilePicLink.url,
      };
      yield call(saveprofilePicInS3, payloadObj);
    }
    yield call(client.mutate.bind(client), {
      mutation: UPDATE_COACH,
      variables: { id, firstName, lastName, tp, timeZone },
    });
    yield put(updateCoachSuccess());
    yield put(fetchCoachRequest());
    toast.success(i18n.t('settings.personalInfo.toastMessage.profileUpdated'), toastStyles.success);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(updateCoachFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchFetchCoachSettings({ payload }: PayloadAction<string>) {
  try {
    const result: IGraphQLResponse<IGetCoachSettingsResponse> = yield call(
      client.query.bind(client),
      { query: GET_COACH_SETTINGS, variables: { coachId: payload } }
    );
    yield put(fetchCoachSettingsSuccess(result.data.getCoachSettings));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchCoachSettingsFailure(error.message));
  }
}

function* watchUpdateCoachSettings({ payload }: PayloadAction<IUpdateCoachAISettings>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: UPDATE_COACH_SETTINGS,
      variables: { coachSettingsInput: payload },
    });
    yield put(updateCoachSettingsSuccess(payload));
    toast.success(i18n.t('settings.appSettings.toastMessage.settingsUpdated'), toastStyles.success);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(updateCoachSettingsFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchGetGoogleSignInURL({
  payload,
}: PayloadAction<{ identityProvider: IdentityProviderEnum }>): Generator<
  CallEffect | PutEffect,
  void,
  IRedirectUrlReponse
> {
  try {
    const { identityProvider } = payload;
    const response = yield call(redirectUrl, identityProvider);
    const data = yield response.json();
    if (response.ok && data.redirectUrl) {
      yield put(getSocialSignInURLSuccess(data.redirectUrl));
    }
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getSocialSignInURLFailure());
  }
}

function* watchGoogleSignIn({
  payload,
}: PayloadAction<{ code: string; currentLocation?: string; brandTag?: string }>): Generator<
  CallEffect | PutEffect,
  void,
  ILoginResponse
> {
  try {
    const { code, brandTag, currentLocation } = payload;
    const response = yield call(socialSignIn, code, currentLocation, brandTag);
    const data = yield response.json();
    if (response.ok) {
      yield put(socialSignInSuccess(data as ILoggedUser));
      if (data.userType === UserType.COACH) {
        yield put(fetchCoachRequest());
      }
      if (data.userType === UserType.ADMIN) {
        yield put(setAdminLoginSuccess());
      }
    } else {
      // get status code
      if (response.status === 401) {
        toast.error(i18n.t('auth.toastMessage.loginUnsuccessful'), toastStyles.error);
      }
      throw new Error(data.message || 'Failed to login');
    }
  } catch (error: unknown) {
    if (error instanceof Error) yield put(socialSignInFailure(error.toString()));
  }
}

function* watchCoachDataRequest({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: COACH_DATA_REQUEST,
      variables: { id: payload.coachId },
    });
    yield put(coachDataRequestSuccess());
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(coachDataRequestFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(i18n.t('settings.privacySettings.dataRequest.requestError'), toastStyles.error);
      }
    }
  }
}

function* watchCoachDeleteRequest({
  payload,
}: PayloadAction<{ coachId: string; password?: string }>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: COACH_DELETE_REQUEST,
      variables: { requestCoachDeleteAccount: payload },
    });
    yield put(coachDeleteRequestSuccess());
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(coachDeleteRequestFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchGetServicePackages({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const result: IGraphQLResponse<IReturnCoachServicePackages> = yield call(
      client.query.bind(client),
      {
        query: GET_COACH_SERVICE_PACKAGES,
        variables: { coachId: payload.coachId },
      }
    );
    yield put(getCoachServicePackagesSuccess(result.data.getCoachServicePackages.servicePackages));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getCoachServicePackagesFailure(error.message));
      toast.error(error.message, toastStyles.error);
    }
  }
}

function* watchCreateServicePackage({ payload }: PayloadAction<ICreateServicePackageInput>) {
  try {
    const result: IGraphQLResponse<ICreateServicePackageResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: CREATE_COACH_SERVICE_PACKAGE,
        variables: { CreateServicePackage: payload },
      }
    );
    yield put(createCoachServicePackageSuccess(result.data.createServicePackage));
    toast.success(t('bookings.packageAdded'), toastStyles.success);
  } catch (error: unknown) {
    const graphQLError = getGraphQLError(error);
    if (graphQLError) {
      toast.error(graphQLError.message, toastStyles.error);
      yield put(createCoachServicePackageFailure(graphQLError.message));
    } else {
      yield put(createCoachServicePackageFailure(t('auth.toastMessage.unknownError')));
    }
  }
}

function* watchUpdateServicePackage({ payload }: PayloadAction<IUpdateServicePackageInput>) {
  try {
    const result: IGraphQLResponse<IUpdateServicePackageResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: UPDATE_COACH_SERVICE_PACKAGE,
        variables: { UpdateServicePackage: payload },
      }
    );
    yield put(updateCoachServicePackageSuccess(result.data.updateServicePackage));
    toast.success(t('bookings.packageUpdated'), toastStyles.success);
  } catch (error: unknown) {
    const graphQLError = getGraphQLError(error);
    if (graphQLError) {
      toast.error(graphQLError.message, toastStyles.error);
      yield put(updateCoachServicePackageFailure(graphQLError.message));
    } else {
      yield put(updateCoachServicePackageFailure(t('auth.toastMessage.unknownError')));
    }
  }
}

function* watchDeleteServicePackage({ payload }: PayloadAction<{ id: string; coachId: string }>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: DELETE_COACH_SERVICE_PACKAGE,
      variables: { DeleteServicePackage: payload.id },
    });
    yield put(deleteCoachServicePackageSuccess(payload.id));
    toast.error(t('bookings.packageDeleted'), toastStyles.error);
  } catch (error: unknown) {
    const graphQLError = getGraphQLError(error);
    if (graphQLError) {
      toast.error(graphQLError.message, toastStyles.error);
      yield put(deleteCoachServicePackageFailure(graphQLError.message));
    } else {
      yield put(deleteCoachServicePackageFailure(t('auth.toastMessage.unknownError')));
    }
  }
}

function* watchGetCoachEventDetails({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const result = yield call(client.query.bind(client), {
      query: GET_COACH_EVENT_DETAILS,
      variables: { coachId: payload.coachId },
    });
    if (result.data.getCoachEventSettings) {
      yield put(getCoachEventDetailsSuccess(result.data.getCoachEventSettings));
    }
    yield put(getCoachEventDetailsFailure(result.error));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getCoachEventDetailsFailure(error.message));
    }
  }
}

function* watchUpdateCoachEventDetails({ payload }: PayloadAction<IAddCoachEventSettingsInput>) {
  try {
    const result = yield call(client.mutate.bind(client), {
      mutation: UPDATE_COACH_EVENT_DETAILS,
      variables: { updateCoachEventSettingsInput: payload },
    });
    yield put(updateCoachEventDetailsSuccess(result.data.updateCoachEventSettings));
    toast.success('Event limits updated successfully.', toastStyles.success);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(updateCoachEventDetailsFailure(error.message));
      toast.error(error.message, toastStyles.error);
    }
  }
}

function* watchGetBrandConfigurations({
  payload,
}: PayloadAction<{ brandTag: string; navigate: NavigateFunction }>) {
  try {
    const result: IGraphQLResponse<IGetBrandConfigurationsResponse> = yield call(
      client.query.bind(client),
      { query: GET_BRAND_CONFIGURATIONS, variables: { brandTag: payload.brandTag } }
    );
    yield put(getBrandConfigurationsSuccess(result.data.getBrandConfigurations));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getBrandConfigurationsFailure());
      payload.navigate(SIGNUP_ROUTE);
      removeBrandConfigurations();
    }
  }
}

function* watchCreatePasswordRequest({ payload }: PayloadAction<ICreatePasswordInput>) {
  try {
    const result = yield call(createCoachPassword, payload);
    if (result.status === 200) {
      window.location.href = LOGIN_ROUTE;
      yield put(createPasswordSuccess());
    } else {
      yield put(createPasswordFailure());
    }
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const res = error.response.data;
      toast.error(res.message, toastStyles.error);
    } else {
      toast.error(t('auth.toastMessage.unknownError'), toastStyles.error);
    }
    yield put(createPasswordFailure());
  }
}

export function* watchCoachSagas() {
  yield takeLatest(fetchCoachRequest, watchFetchCoach);
  yield takeLatest(fetchArchivedCoaches, watchFetchCoaches);
  yield takeLatest(fetchUpdateCoachLanguageRequest, watchUpdateCoachLanguage);
  yield takeLatest(coachLoginRequest, watchCoachLogin);
  yield takeLatest(passwordResetRequest, watchPasswordReset);
  yield takeLatest(fetchCoachesRequest, watchFetchCoaches);
  yield takeLatest(approveCoachRequest, watchApproveCoach);
  yield takeLatest(addCoachRequest, watchAddCoach);
  yield takeLatest(rejectCoachRequest, watchRejectCoach);
  yield takeLatest(changeCoachStatus, watchChangeCoachStatus);
  yield takeLatest(archiveCoachRequest, watchArchieveCoachRequest);
  yield takeLatest(updateCoach, watchUpdateCoach);
  yield takeLatest(fetchCoachSettings, watchFetchCoachSettings);
  yield takeLatest(updateCoachSettings, watchUpdateCoachSettings);
  yield takeLatest(getSocialSignInURL, watchGetGoogleSignInURL);
  yield takeLatest(socialSignInRequest, watchGoogleSignIn);
  yield takeLatest(coachDataRequest, watchCoachDataRequest);
  yield takeLatest(coachDeleteRequest, watchCoachDeleteRequest);
  yield takeLatest(getBrandConfigurations, watchGetBrandConfigurations);
  yield takeLatest(getCoachServicePackagesRequest, watchGetServicePackages);
  yield takeLatest(createCoachServicePackageRequest, watchCreateServicePackage);
  yield takeLatest(updateCoachServicePackageRequest, watchUpdateServicePackage);
  yield takeLatest(deleteCoachServicePackageRequest, watchDeleteServicePackage);
  yield takeLatest(getCoachEventDetailsRequest, watchGetCoachEventDetails);
  yield takeLatest(updateCoachEventDetailsRequest, watchUpdateCoachEventDetails);
  yield takeLatest(createPasswordRequest, watchCreatePasswordRequest);
}

const coachSagas = watchCoachSagas;
export default coachSagas;
